// Round 3 (R3-H01 / INV-11): origin-wide feedback identity epoch.
//
// One monotonically increasing epoch, stored under a stable versioned key,
// fences every feedback autosave, recovery write, and pending-mutation
// callback: a stale tab or late async response from an earlier epoch can never
// reactivate or overwrite outgoing-actor state after an identity transition in
// another tab. Increments are serialized with the Web Locks API where
// supported, with a short owner-token/expiry localStorage lease fallback, and
// the committed record is REREAD before use — the stored record, never a
// tab-local counter, is authoritative.

export const FEEDBACK_IDENTITY_STORAGE_KEY = 'dha_nexus_feedback_identity:v1';
const CHANNEL_NAME = 'dha_nexus_feedback_identity';
const LOCK_NAME = 'dha-nexus-feedback-identity';
const LEASE_KEY = `${FEEDBACK_IDENTITY_STORAGE_KEY}:lease`;
const LEASE_TTL_MS = 1500;

function getStorage(storage) {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  return window.localStorage;
}

function parseRecord(raw) {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const epoch = Number(value.identityEpoch);
    if (!Number.isInteger(epoch) || epoch < 1) return null;
    return {
      ownerUserId: typeof value.ownerUserId === 'string' ? value.ownerUserId : null,
      identityEpoch: epoch,
      activationState: value.activationState === 'active' ? 'active' : 'inactive',
      transitionedAt: value.transitionedAt || null,
    };
  } catch {
    return null;
  }
}

export function readFeedbackIdentityRecord(storage) {
  const target = getStorage(storage);
  if (!target) return null;
  try {
    return parseRecord(target.getItem(FEEDBACK_IDENTITY_STORAGE_KEY));
  } catch {
    return null;
  }
}

let channel = null;
function broadcastChannel() {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      channel = null;
    }
  }
  return channel;
}

function randomToken() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function commitAdvance(target, { ownerUserId, activationState }) {
  const previous = parseRecord(target.getItem(FEEDBACK_IDENTITY_STORAGE_KEY));
  const nextState = activationState === 'active' ? 'active' : 'inactive';
  // Idempotent under the lock: the epoch fences state TRANSITIONS, and two
  // racing requests for the same owner+state (the explicit session start and
  // the snapshot-persist effect's self-activation) are one transition, not
  // two. Advancing again would fence the very capture/save the first
  // activation just authorized — observed as a silently discarded first
  // capture when the second advance landed mid-frame.
  if (
    previous &&
    (previous.ownerUserId ?? null) === (ownerUserId ?? null) &&
    previous.activationState === nextState
  ) {
    return previous;
  }
  const record = {
    ownerUserId: ownerUserId ?? null,
    identityEpoch: (previous?.identityEpoch || 0) + 1,
    activationState: nextState,
    transitionedAt: new Date().toISOString(),
  };
  target.setItem(FEEDBACK_IDENTITY_STORAGE_KEY, JSON.stringify(record));
  // The stored record is authoritative: if a concurrent writer advanced past
  // us between our write and this read, ITS record wins.
  const committed = parseRecord(target.getItem(FEEDBACK_IDENTITY_STORAGE_KEY)) || record;
  return committed;
}

async function withLease(target, work) {
  const token = randomToken();
  for (let attempt = 0; attempt < 5; attempt += 1) {
    let lease = null;
    try {
      lease = JSON.parse(target.getItem(LEASE_KEY) || 'null');
    } catch {
      lease = null;
    }
    const leaseFresh = lease && Number.isFinite(lease.at) && Date.now() - lease.at < LEASE_TTL_MS;
    if (!leaseFresh) break;
    await sleep(25);
  }
  try {
    target.setItem(LEASE_KEY, JSON.stringify({ token, at: Date.now() }));
    // Confirm we hold the lease before committing (last writer wins the slot).
    let confirmed = null;
    try {
      confirmed = JSON.parse(target.getItem(LEASE_KEY) || 'null');
    } catch {
      confirmed = null;
    }
    if (confirmed?.token !== token) {
      // Another tab took the lease after us: brief wait, then proceed anyway —
      // commitAdvance rereads the stored record, so the result stays monotonic.
      await sleep(30);
    }
    return work();
  } finally {
    try {
      const current = JSON.parse(target.getItem(LEASE_KEY) || 'null');
      if (current?.token === token) target.removeItem(LEASE_KEY);
    } catch {
      /* lease cleanup is best-effort; the TTL bounds a stuck lease */
    }
  }
}

export async function advanceFeedbackIdentityEpoch(
  { ownerUserId = null, activationState = 'inactive' } = {},
  storage,
) {
  const target = getStorage(storage);
  if (!target) return null;
  const commit = () => commitAdvance(target, { ownerUserId, activationState });
  let committed;
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    committed = await navigator.locks.request(LOCK_NAME, async () => commit());
  } else {
    committed = await withLease(target, commit);
  }
  const bus = broadcastChannel();
  if (bus) {
    try {
      bus.postMessage(committed);
    } catch {
      /* the localStorage write already reaches other tabs via storage events */
    }
  }
  return committed;
}

export function subscribeFeedbackIdentity(listener) {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (event) => {
    if (event.key !== FEEDBACK_IDENTITY_STORAGE_KEY) return;
    const record = parseRecord(event.newValue);
    if (record) listener(record);
  };
  const bus = broadcastChannel();
  const onMessage = (event) => {
    const record =
      event?.data && typeof event.data === 'object'
        ? parseRecord(JSON.stringify(event.data))
        : null;
    if (record) listener(record);
  };
  window.addEventListener('storage', onStorage);
  if (bus) bus.addEventListener('message', onMessage);
  return () => {
    window.removeEventListener('storage', onStorage);
    if (bus) bus.removeEventListener('message', onMessage);
  };
}
